import axios from 'axios';
import { getIsraelConfig } from '../config/israelConfig';

// No-auth axios instance for the Israel patient app, pointed at the Israel
// backend (the same backend the Israel mobile app uses; see
// config/israelConfig.ts). All endpoints are keyed by an opaque per-patient
// `token` taken from the /schedule/:token URL.
// A DEADLINE on every call. Axios waits forever by default, and on 20/10/2026
// the backend hung on a dead Xano for five minutes per request - the patient
// sat on a spinner with no error and nothing to click. The gateway gives up at
// 60s, so anything longer than that can only ever be a worse version of a 504.
// Slot queries are the slow ones (a live Yoman call); 45s leaves them room
// while still failing before the page looks broken.
const israelApi = axios.create({
  baseURL: getIsraelConfig().israelBackendBaseUrl,
  timeout: 45000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// The mobile app opens the schedule page with ?mobile_app=true. Forward the
// marker on every API call so the backend attributes the request to the app
// (schedule_log device column). Decided per request from the CURRENT page
// URL - no persistence, so a plain link in the same tab is never misread.
israelApi.interceptors.request.use((config) => {
  try {
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('mobile_app') === 'true'
    ) {
      config.params = { ...(config.params || {}), mobile_app: 'true' };
    }
  } catch {
    /* non-browser environment */
  }
  return config;
});

// ?scheduling=uk asks the backend to answer from the UK system instead of
// Yoman, while both exist. Same shape as the marker above - read from the
// CURRENT page URL on every call, never persisted - so it applies for as long
// as the patient stays on that link and stops the moment they do not.
//
// On EVERY call, not just the first, because the page keeps asking: changing
// month, opening a day, then booking are separate requests, and a flag that
// only reached the first one would silently lapse mid-session and let a
// patient book against an engine that never offered them the slot.
//
// Into the body for the two POSTs that take it there - book and reschedule -
// because the backend reads it from the body on those, so that the engine
// which offered a slot is the one asked to validate it.
const UK_BODY_ROUTES = ['/book', '/reschedule'];

// A choice a staff member already made, carried on the link they sent. The
// page forwards it on every call so the backend offers THAT meeting type and
// THAT clinician, instead of working it out from the patient's own state -
// which is right for a patient's own link and wrong for one a colleague sent
// after picking in clinicUI.
const CHOICE_PARAMS = ['meeting_type_id', 'therapist_id', 'meeting_id'];
// Carried the same way, but it is a flag rather than an id, so the digits-only
// rule below would have thrown it away. Without it the page asked for the
// chosen meeting and the backend, not being told this was a NEW booking,
// answered with the patient's whole chain whenever they already had one of
// that kind - which is every real patient.
//
// `blocked` is the second: a coordinator's link asking for the therapist's
// CLOSED hours as well. Only the link carries it - the page has no idea
// whose link it is - so without forwarding it the backend was asked the
// ordinary question and answered with the ordinary slots, which is why
// ticking the box in clinicUI appeared to do nothing. It is an ASK, not a
// grant: the backend honours it only for an issuer holding
// secretary_scheduling, re-checked from the signed token on every request.
const FLAG_PARAMS = ['book', 'blocked'];

/** What the staff link asked for, read off the browser's own URL.
 *
 *  Exported so it can be tested on its own: it is the only thing standing
 *  between a link's choices and the backend, and when it silently drops one
 *  the page answers a different question than the link asked.
 */
export const choicesFromLink = (): Record<string, string> => {
  const out: Record<string, string> = {};
  try {
    if (typeof window === 'undefined') return out;
    const here = new URLSearchParams(window.location.search);
    CHOICE_PARAMS.forEach((key) => {
      const v = here.get(key);
      // Only digits: these end up as ids in a query the backend trusts.
      if (v && /^\d+$/.test(v)) out[key] = v;
    });
    FLAG_PARAMS.forEach((key) => {
      // Only the literal "true" is passed on, so nothing else can ride along.
      if (here.get(key) === 'true') out[key] = 'true';
    });
  } catch {
    /* non-browser environment */
  }
  return out;
};

israelApi.interceptors.request.use((config) => {
  // The link fills in what the CALLER did not say; it never overrules it.
  //
  // The page asks for slots using the type on the item in front of it, which
  // may not be the type on the link: a follow-up asked for after a long gap
  // is booked as an intake. Letting the link win would offer times for one
  // kind of appointment and book the other.
  const carried = choicesFromLink();
  const given = config.params || {};
  const fill = Object.fromEntries(
    Object.entries(carried).filter(([k]) => given[k] === undefined));
  if (Object.keys(fill).length) {
    config.params = { ...given, ...fill };
  }
  return config;
});

israelApi.interceptors.request.use((config) => {
  try {
    if (typeof window === 'undefined') return config;
    const asked = new URLSearchParams(window.location.search).get('scheduling');
    if ((asked || '').trim().toLowerCase() !== 'uk') return config;

    const url = config.url || '';
    const intoBody =
      (config.method || '').toLowerCase() === 'post' &&
      UK_BODY_ROUTES.some((suffix) => url.endsWith(suffix));

    if (intoBody) {
      // config.data is a plain object here (axios serializes it later), but
      // guard anyway: a string body would be silently replaced otherwise.
      if (config.data && typeof config.data === 'object') {
        config.data = { ...config.data, scheduling: 'uk' };
      }
    } else {
      config.params = { ...(config.params || {}), scheduling: 'uk' };
    }
  } catch {
    /* non-browser environment */
  }
  return config;
});

// ---------------------------------------------------------------------------
// Context response
// ---------------------------------------------------------------------------
// GET /public/schedule/{token}
// Returns the patient's name and their list of meetings. This is the mobile
// backend's real shape — a flat `meetings[]` array whose `status` drives the
// whole UI. See the ticket / backend probes:
//   status "active"          -> upcoming meeting; edit+cancel iff can_update
//   status "schedule"        -> placeholder to be booked (id === null)
//   status "future_schedule" -> not bookable yet; shows the API text (grey)
//   status "done"            -> completed/history
export type MeetingStatus =
  | 'active'
  | 'schedule'
  | 'future_schedule'
  | 'done'
  | (string & {});

export interface IsraelMeeting {
  id: number | null; // null for a not-yet-booked "schedule" placeholder
  meetingtype_id: number;
  therapist_type: string; // e.g. "פסיכאטר", "מנהל תיק" — used in card header
  meeting_type: string; // e.g. "מעקב" — used in card header
  length: number; // minutes
  status: MeetingStatus;
  can_update: boolean; // reschedule/cancel allowed
  start: number | null; // epoch ms in the patient's clinic timezone; null if unbooked
  from_date: string | null; // "YYYY-MM-DD" — start of the bookable window
  to_date: string | null; // "YYYY-MM-DD" — end of the bookable window
  user_id: number; // therapist id
  meeting_title_number: number | null; // ordinal within the series
  text: string | null; // pre-localized label (e.g. "לחץ לקביעת פגישה", "בוצעה")
  hint?: string | null; // role hint under the text (e.g. "בסמיכות לפגישת פסיכאטר")
}

export interface IsraelScheduleContext {
  patient_name: string;
  meetings: IsraelMeeting[];
  /** Set when a staff link's appointment has ALREADY been booked.
   *
   *  The link only knows how to offer a booking, so without this the page
   *  showed a second booking screen for an appointment that already exists.
   *  The patient's own view is shown instead, and this says to confirm it. */
  already_booked_meeting_id?: number | null;
}

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------
// A single bookable time slot.
//   value   — opaque token echoed back on book/reschedule (a naive datetime;
//             DO NOT reformat it client-side — timezone is server-applied).
//   display — pre-formatted HH:MM in the clinic's timezone; show this.
//   date    — "YYYY-MM-DD" the slot falls on.
export interface Slot {
  value: string;
  display: string;
  date: string;
  // Therapist (Yoman resource) that owns this slot — present for pool slots;
  // echoed back on book so the event gets a concrete resource.
  resource_id?: string;
  // Hours the therapist has CLOSED, offered anyway because a coordinator
  // asked for them. Present only on those slots, and only in such a
  // session. Booking one opens exactly the meeting's minutes and leaves the
  // rest of the block shut.
  blocked?: boolean;
}

// Response of the first-available-month endpoint.
export interface FirstAvailableMonth {
  month: string | null; // "YYYY-MM"
  from_date: string | null; // "YYYY-MM-DD"
  to_date: string | null; // "YYYY-MM-DD"
  slots: Slot[];
  // Yoman did not answer in time. NOT the same as "no slots": the availability
  // is unknown, so the screen must say so rather than show an empty calendar.
  unavailable?: boolean;
}

// A slot list plus whether it is trustworthy. An empty `slots` with
// `unavailable` set means "we could not find out", not "nothing is free".
export interface SlotsResult {
  slots: Slot[];
  unavailable: boolean;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

// Dedupe concurrent identical GETs: React 18 StrictMode double-runs mount
// effects in dev, and slot fetches are expensive (a live Yoman call each).
// While a GET is in flight, an identical request shares the same promise.
const inflightGets = new Map<string, Promise<unknown>>();

function dedupedGet<T>(
  url: string,
  params?: Record<string, unknown>,
  opts?: { force?: boolean; timeout?: number }
): Promise<T> {
  const key = `${url}?${JSON.stringify(params ?? {})}`;
  // `force` is for a retry the patient asked for. Sharing the in-flight
  // promise is right when two components want the same data at once, and
  // exactly wrong here: the request being shared is the one that just failed
  // them, so "try again" attached to the stuck call and did nothing.
  if (!opts?.force) {
    const existing = inflightGets.get(key);
    if (existing) return existing as Promise<T>;
  }
  const config: Record<string, unknown> = {};
  if (params) config.params = params;
  if (opts?.timeout) config.timeout = opts.timeout;
  const request = israelApi
    .get(url, config)
    .then((res) => res.data as T)
    .finally(() => {
      if (inflightGets.get(key) === request) inflightGets.delete(key);
    });
  inflightGets.set(key, request);
  return request;
}

// Load the patient's schedule context (name + meetings).
export async function getScheduleContext(
  token: string,
  opts?: { force?: boolean }
): Promise<IsraelScheduleContext> {
  // The page cannot render without this, so it gets a tighter deadline than
  // the slot queries: it is a small read, and a patient staring at a spinner
  // needs to be told something is wrong long before 45 seconds.
  return dedupedGet<IsraelScheduleContext>(
    `/public/schedule/${token}`,
    undefined,
    { force: opts?.force, timeout: 15000 }
  );
}

// --- Reschedule an EXISTING meeting (keyed by meeting_id) ------------------

// First month (within the meeting's reschedule window) that has any
// available slots.
export async function getFirstAvailableMonth(
  token: string,
  meetingId: number,
  fromDate?: string | null,
  toDate?: string | null
): Promise<FirstAvailableMonth> {
  return dedupedGet<FirstAvailableMonth>(
    `/public/schedule/${token}/first-available-month`,
    {
      meeting_id: meetingId,
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
    }
  );
}

// First month with availability for a brand-new meeting of this type,
// clamped to the item's booking window when provided.
export async function getNewMeetingFirstAvailableMonth(
  token: string,
  meetingTypeId: number,
  fromDate?: string | null,
  toDate?: string | null
): Promise<FirstAvailableMonth> {
  return dedupedGet<FirstAvailableMonth>(
    `/public/schedule/${token}/first-available-month`,
    {
      meeting_type_id: meetingTypeId,
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
    }
  );
}

// Available reschedule slots for a meeting within a date range (inclusive).
export async function getRescheduleSlots(
  token: string,
  meetingId: number,
  fromDate: string,
  toDate: string
): Promise<SlotsResult> {
  const data = await dedupedGet<{ slots?: Slot[]; unavailable?: boolean }>(
    `/public/schedule/${token}/slots`,
    { meeting_id: meetingId, from_date: fromDate, to_date: toDate }
  );
  return { slots: data?.slots ?? [], unavailable: !!data?.unavailable };
}

// How long a view took to appear, as the PAGE measured it - network and
// render included, which the server's own timings cannot see. Fire and forget:
// a failed measurement must never affect the patient.
export async function reportViewTiming(
  token: string,
  body: {
    view: 'month' | 'day' | 'page';
    ms: number;
    slots?: number;
    date?: string;
    meeting_id?: number | null;
    cold?: boolean;
  }
): Promise<void> {
  try {
    await israelApi.post(`/public/schedule/${token}/timing`, body);
  } catch {
    /* telemetry only */
  }
}

// The answer to a booking request.
//   start  — when the meeting was ACTUALLY booked, ISO with offset. The server
//            verifies the Yoman event before answering, so this is the only
//            trustworthy time to show the patient: Yoman can place an event at
//            a different time than the one asked for.
//   reason — why a booking or move was refused:
//              "slot_taken"    Yoman would not give the slot the patient
//                              picked - something is already booked there.
//              "outside_shift" nothing is booked; the time falls outside the
//                              therapist's working hours.
//              "creates_gap"   the move would strand an unbookable gap.
//            The server withholds a refused start from the next page load, so
//            a refresh shows a list the slot is no longer on.
export interface BookResult {
  ok: boolean;
  yoman_id?: string;
  start?: string;
  reason?: string;
}

// Move an existing meeting to a new slot (start = a slot's `value`).
// waitingList: the patient opted into the waiting list ("contact me if an
// earlier slot frees up") — stored on the meeting's waiting_list field.
export async function rescheduleMeeting(
  token: string,
  meetingId: number,
  start: string,
  waitingList = false
): Promise<BookResult> {
  const { data } = await israelApi.post(
    `/public/schedule/${token}/reschedule`,
    { meeting_id: meetingId, start, waiting_list: waitingList }
  );
  return data;
}

// --- Book a NEW meeting for a "schedule" placeholder -----------------------
// Availability is filtered by meeting_type_id only (user_id was verified NOT
// to affect results). user_id is forwarded on booking so the meeting binds to
// the intended therapist — per the ticket; confirm on the backend if bookings
// land on the wrong therapist.

// Available slots for a brand-new meeting within a date range (inclusive).
export async function getNewMeetingSlots(
  token: string,
  meetingTypeId: number,
  fromDate: string,
  toDate: string,
  userId?: number
): Promise<SlotsResult> {
  const data = await dedupedGet<{ slots?: Slot[]; unavailable?: boolean }>(
    `/public/schedule/${token}/new-slots`,
    {
      meeting_type_id: meetingTypeId,
      from_date: fromDate,
      to_date: toDate,
      ...(userId != null ? { user_id: userId } : {}),
    }
  );
  return { slots: data?.slots ?? [], unavailable: !!data?.unavailable };
}

// Book a brand-new meeting at a chosen slot (start = a slot's `value`).
// resourceId (from the chosen slot) is required for pool slots so the Yoman
// event gets a concrete resource.
// waitingList: the patient opted into the waiting list ("contact me if an
// earlier slot frees up") — stored on the meeting's waiting_list field.
export async function bookMeeting(
  token: string,
  meetingTypeId: number,
  start: string,
  userId?: number,
  resourceId?: string,
  waitingList = false
): Promise<BookResult> {
  const { data } = await israelApi.post(`/public/schedule/${token}/book`, {
    meeting_type_id: meetingTypeId,
    start,
    ...(userId != null ? { user_id: userId } : {}),
    ...(resourceId ? { resource_id: resourceId } : {}),
    waiting_list: waitingList,
  });
  return data;
}

// --- Cancel ----------------------------------------------------------------

// Cancel an existing meeting, optionally with a reason.
export async function cancelMeeting(
  token: string,
  meetingId: number,
  reason?: string
): Promise<{ ok: boolean }> {
  const { data } = await israelApi.post(`/public/schedule/${token}/cancel`, {
    meeting_id: meetingId,
    ...(reason ? { reason } : {}),
  });
  return data;
}

export default israelApi;
